import moment from "moment";
import Radio from "backbone.radio";
import { captureException } from "extensions/sentry";

import { merchant, user } from "common/config/api";
import { store } from "common/store";

// eslint-disable-next-line import/no-cycle
import { request } from "common/components/request";
import { Qualifiers } from "common/components/queryBuilder/qualifiers";
import { getCurrencyDecimals, getCurrencySymbol } from "common/helpers/currency";

export const PARSE_COMPARE_DATES_URL = function({ dates }) {
    if (dates.length) {
        let normalized;
        
        try {
            normalized = dates.split("__").map((d) => {
                const [startDate, endDate] = d.split("_");
                return { startDate, endDate };
            });
        } catch (error) {
            console.error(error);
            normalized = [];
        }
        
        store.dispatch({
            type: "SET_COMPARISON_DATES",
            payload: {
                compare_to: normalized
            }
        });
    }
};

export const SERIALIZE_COMPARE_DATES_URL = function() {
    const dates = store.getState().common.compare_to;
    
    return dates.map(({ startDate, endDate }) => `${moment.utc(startDate).format("YYYY-MM-DD")}_${moment.utc(endDate).format("YYYY-MM-DD")}`).join("__");
};

export const SET_GLOBAL_DATE_RANGE_ONLY = function({ startDate, endDate }) {
    if (startDate.isValid() && endDate.isValid()) {
        store.dispatch({
            type: "SET_GLOBAL_DATE_RANGE_ONLY",
            payload: {
                startDate: startDate.clone().startOf("day"),
                endDate: endDate.clone().endOf("day")
            }
        });
    }
};

export const RESET_GLOBAL_DATE_RANGE = function() {
    store.dispatch({
        type: "RESET_GLOBAL_DATE_RANGE"
    });
};

export const PARSE_DATES_URL = function({ dates }) {
    if (!dates.length) return;
    
    try {
        const [startDate, endDate] = dates.split("_").map((d) => moment.utc(d));
    
        SET_GLOBAL_DATE_RANGE_ONLY({ startDate, endDate });
    } catch (error) {
        console.error(error);
    }
};

export const PARSE_DATETYPE_URL = function({ datetype }) {
    if (!datetype.length) return;
    
    try {
        SET_GLOBAL_DATETYPE({ datetype });
    } catch (error) {
        console.error(error);
    }
};

export const SET_GLOBAL_DATETYPE = function({ datetype }) {
    if (store.getState().common.date_range.datetype === datetype) return;
    
    store.dispatch({
        type: "SET_GLOBAL_DATETYPE",
        datetype
    });
};

export const PARSE_CURRENCY_URL = function({ currency }) {
    if (!currency.length) return;
    
    store.dispatch(SET_NORMALIZING_CURRENCY({
        currency,
        currency_source: "url"
    }));
};

export const SET_DATES_FROM_MERCHANT = function() {
    const [startDate, endDate] = Radio.channel("main").request("get:merchant").getRootMerchantProperty("date_range");
    
    SET_GLOBAL_DATE_RANGE_ONLY({
        startDate: moment.utc(startDate),
        endDate: moment.utc(endDate)
    });
};

export const SERIALIZE_DATES_FROM_STORE = function() {
    const state = store.getState();
    const { startDate, endDate, datetype } = state.common.date_range;
    
    const momentStart = moment.utc(startDate);
    const momentEnd = moment.utc(endDate);
    
    const returnObject = {};
    
    if (momentStart.isValid() && momentEnd.isValid()) {
        returnObject.dates = `${momentStart.format("YYYY-MM-DD")}_${momentEnd.format("YYYY-MM-DD")}`;
    }
    
    if (datetype) {
        returnObject.datetype = datetype;
    }
    
    return returnObject;
};

export const SET_NORMALIZING_CURRENCY = function({ currency, currency_source }) {
    return (dispatch, getState) => {
        let currency_symbol;
        let currency_decimals = 2;
    
        try {
            const activeUser = Radio.channel("main").request("get:user");
            activeUser.fetchPrerequisites.then(() => {
                currency_symbol = getCurrencySymbol(currency);
                currency_decimals = getCurrencyDecimals(currency);
            
                dispatch({
                    type: "SET_NORMALIZING_CURRENCY",
                    currency,
                    currency_symbol,
                    currency_decimals,
                    currency_source
                });
            });
        } catch (error) {
            // noop
            captureException(error);
        }
    };
};

export const SET_BU_REF = function(bu_ref) {
    return (dispatch, getState) => {
        const state = getState();
        
        if (state.common.bu_ref === bu_ref) return;
        
        dispatch({
            type: "SET_BU_REF",
            bu_ref
        });
    };
};

export const SET_MERCHANT_REF = function(merchant_ref, clear_bu_ref = true) {
    return (dispatch, getState) => {
        dispatch({
            type: "SET_MERCHANT_REF",
            merchant_ref,
            clear_bu_ref
        });
    };
};

export const SET_BU_AND_MERCHANT_REF = function({ merchant_ref, bu_ref }) {
    return (dispatch, getState) => {
        dispatch({
            type: "SET_BU_AND_MERCHANT_REF",
            merchant_ref,
            bu_ref
        });
    };
};

export const SET_MERCHANT_CONFIG = function({ config = {} }) {
    store.dispatch({
        type: "SET_MERCHANT_CONFIG",
        config
    });
};

export const ADD_TO_HISTORY_STACK = function({ name, fragment }) {
    store.dispatch({
        type: "ADD_TO_HISTORY_STACK",
        data: {
            name,
            fragment,
            timestamp: moment().toISOString()
        }
    });
};

export const CLEAR_HISTORY_STACK = function() {
    store.dispatch({
        type: "CLEAR_HISTORY_STACK"
    });
};

export const SET_GLOBAL_DATE_RANGE = function({ params }) {
    store.dispatch({
        type: "SET_GLOBAL_DATE_RANGE",
        payload: params
    });
};

// This method does not require to have dates momentized
export const SET_GLOBAL_DATE_RANGE_RAW = function(params) {
    store.dispatch({
        type: "SET_GLOBAL_DATE_RANGE_RAW",
        payload: params
    });
};

export const REMOVE_COMPARISON_DATE = function({ index }) {
    const state = store.getState();
    
    store.dispatch({
        type: "SET_COMPARISON_DATES",
        payload: {
            compare_to: state.common.compare_to.filter((d, i) => index !== i)
        }
    });
};

export const RESET_COMPARISON_DATES = function() {
    return (dispatch) => {
        dispatch({
            type: "SET_COMPARISON_DATES",
            payload: {
                compare_to: []
            }
        });
    };
};

export const FETCH_MAPPINGS_IF_MISSING = function() {
    const state = store.getState();
    
    if (state.common.mappings_fetched) {
        return Promise.resolve(state.common.mappings);
    }
    
    return request("/zoolander/mappings/all").then((mappings) => {
        store.dispatch({
            type: "SET_ZOOLANDER_ALL",
            mappings
        });
        
        return mappings;
    });
};

export const DOWNLOAD_REPORT = function(reportId) {
    return (dispatch) => request(user.exchangeToken(reportId), {
        method: "POST"
    }).then((data) => {
        if (data.download_uri) {
            setTimeout(() => {
                window.location.href = data.download_uri;
            }, 2000); // We need timeout to handle download of multiple files
        } else {
            throw new Error("Response does not contain the download_uri");
        }
    });
};

export const FETCH_ADMINS = function() {
    return (dispatch) => {
        dispatch({
            type: "START_FETCHING_ADMINS"
        });
    
        request(merchant.admins).then((admins = []) => {
            dispatch({
                type: "SUCCESS_FETCHING_ADMINS",
                data: admins
            });
        }, () => {
            dispatch({
                type: "ERROR_FETCHING_ADMINS"
            });
        });
    };
};

export const SET_ACTIVE_USER = function(userData) {
    store.dispatch({
        type: "SET_ACTIVE_USER",
        data: userData
    });
};

export const SET_AVAILABLE_MERCHANTS = function(merchants) {
    store.dispatch({
        type: "SET_AVAILABLE_MERCHANTS",
        data: merchants
    });
};

export const REQUESTS_SET_ACTIVE_PRODUCT = function(product) {
    store.dispatch({
        type: "REQUESTS_SET_ACTIVE_PRODUCT",
        data: product
    });
};

export const SET_ACTIVE_ROUTE = function(route) {
    store.dispatch({
        type: "SET_ACTIVE_ROUTE",
        route
    });
};

export const SET_ACTIVE_PAGE_NAME = function(name) {
    store.dispatch({
        type: "SET_ACTIVE_PAGE_NAME",
        name
    });
};

export const SET_ACTIVE_QUALIFIERS = function(qualifiersStr = "") {
    store.dispatch({
        type: "SET_ACTIVE_QUALIFIERS",
        qualifiersStr
    });
};

export const ADD_QUALIFIER = function(newQualifier) {
    const qualifiers = new Qualifiers({
        value: store.getState().common.qualifiersStr
    });
    qualifiers.addOrReplace(newQualifier);
    
    store.dispatch({
        type: "SET_ACTIVE_QUALIFIERS",
        qualifiersStr: qualifiers.toString()
    });
};

export const REMOVE_QUALIFIER = function(newQualifier) {
    const qualifiers = new Qualifiers({
        value: store.getState().common.qualifiersStr
    });
    qualifiers.remove(newQualifier);

    store.dispatch({
        type: "SET_ACTIVE_QUALIFIERS",
        qualifiersStr: qualifiers.toString()
    });
};

export const FETCH_SDR_VERSIONS = function(value) {
    return request("/sdr/version");
};

export const SET_HAS_CHANGES = function(has_unsaved_changes) {
    return (dispatch) => {
        dispatch({
            type: "SET_HAS_CHANGES",
            has_unsaved_changes
        });
    };
};

export const FETCH_USER_MERCHANT = function() {
    return (dispatch, getState) => request(`/merchant/${getState().common.user.merchant_ref}/configuration/settings/`).then((data) => {
        dispatch({
            type: "USER_SET_MERCHANT_CONFIG",
            data
        });
        
        return data;
    });
};
